import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {MatCardModule} from "@angular/material/card";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatButtonModule} from "@angular/material/button";
import {DOCUMENT, JsonPipe, NgForOf, NgIf, NgStyle} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute, Router, RouterLink, NavigationStart} from "@angular/router";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {Title} from "@angular/platform-browser";
import {hymnsDetailed} from "../hymn/hymnsDetailed";
import {MidiPlayerService} from "../service/midi-player.service";
import {LayoutService} from "../service/layout.service";
import {ShareService} from "../service/share.service";
import {HymnSelectionService} from "../service/hymn-selection.service";
import {MatMenuModule} from "@angular/material/menu";
import {SidenavService} from "../service/sidenav.service";
import {FontSizeComponent} from "../controls/font-size/font-size.component";
import {SettingsService} from "../service/settings.service";
import {ContinuousPlaybackService} from "../service/continuous-playback.service";
import {AutoScrollService} from "../service/auto-scroll.service";
import {MusicControlComponent} from "../controls/music-control/music-control.component";
import {ModalService} from "../service/modal.service";
import {Hymn} from "../hymn/hymn";
import {translation} from "../translation";
import {DoubleTapDirective} from "../directive/double-tap.directive";
import {SwipeGestureDirective} from "../directive/swipe-gesture.directive";
import {PinchZoomDirective} from "../directive/pinch-zoom.directive";
import { ScrollContainerDirective } from "../directive/scroll-container.directive";
import { KaraokeMarkupPipe } from "../pipes/karaoke-markup.pipe";

@Component({
  selector: 'app-hymn-reader',
  standalone: true,
  imports: [
    MatIconModule,
    MatCardModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    RouterLink,
    NgIf,
    NgForOf,
    NgStyle,
    MusicControlComponent,
    JsonPipe,
    DoubleTapDirective,
    SwipeGestureDirective,
    PinchZoomDirective,
    KaraokeMarkupPipe,
  ],
  templateUrl: './hymn-reader.component.html',
  styleUrl: './hymn-reader.component.scss'
})
export class HymnReaderComponent implements OnInit, OnDestroy, AfterViewInit {

  // Whether this hymn has karaoke markers (k-chunk spans)
  public hasKaraokeMarkers: boolean = true;

  private karaokeIndex: Map<string, HTMLElement[]> = new Map();
  private activeKeys = new Set<string>();
  private keyToElements = new Map<string, HTMLElement[]>();
  private subscriptions: any[] = [];

  // O(1) Memory Map for lightning-fast boundary lookups during playback
  private sectionDataMap = new Map<string, { lastKeys: Set<string>, nextElement: HTMLElement | null }>();
  private autoJumpTimeout: any;

  // Cache the ordered spans from the DOM to easily peek ahead
  private orderedKaraokeSpans: HTMLElement[] = [];

  @ViewChild('headerChorus') public headerChorus!: ElementRef;
  @ViewChild('headerContent') public headerContent!: ElementRef;
  @ViewChild('chorus') public chorus!: ElementRef;
  @ViewChild('content_container') public content!: ElementRef;
  @ViewChild('scrollWrapper') public scrollWrapper!: ElementRef;

  hymn: any = null;
  elem: any;
  fontSizeBase = this.settingService.getReadModeFontSize();
  fontSize = this.fontSizeBase;
  fontSizeStyle = this.calculateFontSizeStyle();
  fontSizeMax= 100;
  fontSizeMin = 8;
  lineHeightMultiplier = 1.5;
  lineHeightStyle = this.calculateLineHeightStyle();
  lastHymnNumber = hymnsDetailed[hymnsDetailed.length - 1].hymnNumber;

  // Font family and weight for read mode
  readFontFamily: string | undefined = this.settingService.getFontFamily();
  readFontWeight: number | undefined = this.settingService.getFontWeight();

  // Pointer events state
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeStartTime = 0;
  private activePointers = new Map<number, PointerEvent>();

  // Bound handlers for add/remove
  private peDown?: (ev: PointerEvent) => void;
  private peMove?: (ev: PointerEvent) => void;
  private peUpCancel?: (ev: PointerEvent) => void;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private titleService: Title,
    public router: Router,
    public midiService: MidiPlayerService,
    public hymnSelectionService: HymnSelectionService,
    public layoutService: LayoutService,
    public shareService: ShareService,
    public sidenavService: SidenavService,
    protected settingService: SettingsService,
    protected autoScrollService: AutoScrollService,
    protected modalService: ModalService,
    protected continuousPlaybackService: ContinuousPlaybackService) {
  }

  ngAfterViewInit(): void {
    const el = this.scrollWrapper?.nativeElement as HTMLElement | undefined;
    if (el && (!el.style.touchAction || el.style.touchAction === 'auto')) {
      el.style.touchAction = 'pan-y';
    }
    setTimeout(() => this.rebuildKaraokeIndex(), 0);
  }

  private rebuildKaraokeIndex(): void {
    // Sync reader mode with user's persisted default first (discard any previous hymn's fallback)
    const defaultMode = this.settingService.getAutoscroll();
    this.autoScrollService.setMode(defaultMode);

    this.clearActive();
    this.karaokeIndex.clear();
    this.keyToElements.clear();
    this.sectionDataMap.clear();

    const container = this.content?.nativeElement as HTMLElement | null;
    if (!container) return;

    const spans = container.querySelectorAll('.k-chunk[data-k]');
    this.orderedKaraokeSpans = Array.from(spans) as HTMLElement[];

    // Update availability flag and enforce fallback if needed
    this.hasKaraokeMarkers = this.orderedKaraokeSpans.length > 0;
    if (this.autoScrollService.mode === 'off') {
      // Keep it off if user (or default) says off
    } else if (!this.hasKaraokeMarkers && this.autoScrollService.mode === 'karaoke') {
      // Fall back to autoscroll for this hymn only (do not persist setting)
      this.autoScrollService.setMode('autoscroll');
    }

    this.orderedKaraokeSpans.forEach((el: HTMLElement) => {
      const data = el.dataset['k'] || el.getAttribute('data-k') || '';
      if (!data) return;
      const parts = data.split('|').map(s => s.trim()).filter(Boolean);
      for (const key of parts) {
        const arr = this.karaokeIndex.get(key) || [];
        arr.push(el);
        this.karaokeIndex.set(key, arr);
        this.keyToElements.set(key, this.karaokeIndex.get(key)!);
      }
    });

    const sectionContainers = container.querySelectorAll('[data-verse], [data-chorus="true"]');

    for (let i = 0; i < sectionContainers.length; i++) {
      const sec = sectionContainers[i] as HTMLElement;
      const isChorus = sec.getAttribute('data-chorus') === 'true';
      const vNum = isChorus ? sec.getAttribute('data-chorus-for-verse') : sec.getAttribute('data-verse');

      const sectionKey = (isChorus ? 'c' : 'v') + vNum;

      const secSpans = sec.querySelectorAll('.k-chunk[data-k]');
      if (secSpans.length === 0) continue;

      const lastSpan = secSpans[secSpans.length - 1] as HTMLElement;
      const lastData = lastSpan.dataset['k'] || lastSpan.getAttribute('data-k') || '';
      const lastKeys = new Set(lastData.split('|').map(s => s.trim()).filter(Boolean));

      let nextElement: HTMLElement | null = null;
      if (i + 1 < sectionContainers.length) {
        const nextSec = sectionContainers[i + 1] as HTMLElement;
        const nextSpans = nextSec.querySelectorAll('.k-chunk[data-k]');
        if (nextSpans.length > 0) {
          nextElement = nextSpans[0] as HTMLElement;
        }
      }

      this.sectionDataMap.set(sectionKey, { lastKeys, nextElement });
    }
  }

  private clearActive(): void {
    if (this.activeKeys.size === 0) return;
    for (const key of Array.from(this.activeKeys)) {
      const els = this.karaokeIndex.get(key) || [];
      for (const el of els) el.classList.remove('k-active');
    }
    this.activeKeys.clear();
  }

  private isInActiveSection(el: HTMLElement): boolean {
    const currentVerse = this.midiService.currentVerse;
    const playingChorus = this.midiService.playingChorus;

    if (playingChorus) {
      const chorusEl = el.closest('[data-chorus="true"]') as HTMLElement | null;
      if (!chorusEl) return false;
      const forVerse = chorusEl.getAttribute('data-chorus-for-verse');
      return forVerse === String(currentVerse) || (currentVerse === 0 && forVerse === '0');
    }

    const verseEl = el.closest('[data-verse]') as HTMLElement | null;
    return !!verseEl && verseEl.getAttribute('data-verse') === String(currentVerse);
  }

  private setHighlight(track: number, index: number, on: boolean): void {
    if (!this.content) return;
    const key = `t:${track}:${index}`;
    const all = this.karaokeIndex.get(key) || [];
    const els = all.filter(el => this.isInActiveSection(el));

    if (on) {
      if (!this.activeKeys.has(key)) {
        for (const el of els) el.classList.add('k-active');
        this.activeKeys.add(key);
      }
    } else {
      if (this.activeKeys.has(key)) {
        for (const el of els) el.classList.remove('k-active');
        this.activeKeys.delete(key);
      }
    }
  }

  private getCurrentSectionKey(): string {
    const v = this.midiService.currentVerse;
    const c = this.midiService.playingChorus;
    return (c ? 'c' : 'v') + v;
  }

  private isLastLyricOfCurrentVerse(track: number, index: number): boolean {
    const secData = this.sectionDataMap.get(this.getCurrentSectionKey());
    if (!secData) return false;
    return secData.lastKeys.has(`t:${track}:${index}`);
  }

  private getFirstLyricOfNextVerse(): HTMLElement | null {
    const secData = this.sectionDataMap.get(this.getCurrentSectionKey());
    return secData ? secData.nextElement : null;
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.ngZone.run(() => {
      if (!this.hymnSelectionService.searchFocus && !this.hymnSelectionService.dialpadActive) {
        switch (event.key) {
          case 'ArrowLeft':
          case 'b':
            this.onSwipePrevious();
            break;
          case 'ArrowRight':
          case 'n':
            this.onSwipeNext();
            break;
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpad();
            break;
          case 'l':
          case 'L':
            this.sidenavService.toggleList();
            break;
          case 'w':
          case 'W':
            this.hymnSelectionService.goToRandomHymn(false);
            break;
          case 'f':
          case 'F':
            this.layoutService.fullscreen ?
              this.layoutService.exitFullScreen() : this.layoutService.enterFullScreen();
            break;
          case 'g':
          case 'G':
            this.hymnSelectionService.goToRandomHymn(true);
            break;
          case 's':
          case 'S':
            if(this.midiService.playing) {
              this.midiService.stop()
            } else {
              if(this.autoScrollService.enabled) {
                this.autoScrollService.startAutoScroll();
              }
              this.midiService.playMidi(this.hymn, this.settingService.getInstruments(), this.hymn.verses.length);
            }
            break;
          case 'a':
          case 'A':
            this.autoScrollService.active? this.autoScrollService.stopAutoScroll() : this.autoScrollService.startAutoScroll();
            break;
          case 'v':
          case 'V':
            this.router.navigate([`/${translation.path_present}/`+this.hymn.hymnNumber]);
            break;
          case '+':
            if(this.fontSize < this.fontSizeMax) {
              this.updateFontSize(this.fontSize + 1);
            }
            break;
          case '-':
            if(this.fontSize > this.fontSizeMin) {
              this.updateFontSize(this.fontSize - 1);
            }
            break;
          case '<':
          case ',':
            this.midiService.decreasePlaybackSpeed();
            break;
          case '>':
          case '.':
            this.midiService.increasePlaybackSpeed();
            break;
          case 'm':
            this.midiService.setPlaybackSpeed(1.0);
            break;
          case 'i':
            if(!this.modalService.active) {
              this.modalService.openDialog('shortcuts')
            } else {
              this.modalService.closeDialog();
            }
            break;
          case 'Escape':
            if(this.modalService.active) {
              this.modalService.closeDialog();
            }
            break;
        }
      } else {
        switch (event.key) {
          case 'd':
          case 'D':
            this.hymnSelectionService.dialpadCancel();
            break;
          case 'Escape':
            this.hymnSelectionService.dialpadCancel();
            this.hymnSelectionService.searchFocus = false;
            break;
        }
      }
    });
  }


  calculateFontSizeStyle() {
    return this.fontSize + 'px';
  }

  calculateLineHeightStyle() {
    return this.fontSize * this.lineHeightMultiplier + 'px';
  }

  toggleListSidenav() {
    this.sidenavService.toggleList();
  }

  toggleSettingSidenav() {
    this.settingService.toggleSettings();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.midiService.event$.subscribe(ev => {
        if (!ev) return;

        if (ev.type === 'note') {
          const autoScrollOk = (this.autoScrollService.enabled || this.autoScrollService.active);
          if (!autoScrollOk) return;

          if (this.midiService.introPlaying) return;

          const on = ev.state === 'on';

          if (this.autoScrollService.mode === 'karaoke') {
            if (on) {
              this.ngZone.runOutsideAngular(() => this.setHighlight(ev.track, ev.index, true));

              const key = `t:${ev.track}:${ev.index}`;
              const all = this.karaokeIndex.get(key) || [];
              const els = all.filter(el => this.isInActiveSection(el));
              if (els.length > 0) {
                this.autoScrollService.scrollToElement(els[0], this.settingService.getKaraokeScrollDuration());
              }

              if (this.isLastLyricOfCurrentVerse(ev.track, ev.index)) {
                const nextLyric = this.getFirstLyricOfNextVerse();
                if (nextLyric) {
                  this.autoJumpTimeout = setTimeout(() => {
                    if (this.autoScrollService.active || this.autoScrollService.enabled) {
                      this.autoScrollService.scrollToElement(nextLyric, this.settingService.getKaraokeScrollDuration() - 500);
                    }
                  }, 500);
                }
              }
            } else {
              this.ngZone.runOutsideAngular(() => this.setHighlight(ev.track, ev.index, false));
            }
          }
        } else if (ev.type === 'status') {
          clearTimeout(this.autoJumpTimeout);
          const s = ev.status;

          // FIX: Added 'playing' and 'stopped' / 'paused' handlers
          if (s === 'playing') {
            this.autoScrollService.scrollToTop(true);
          } else if (s === 'stopped' || s === 'ended' || s === 'paused') {
            this.autoScrollService.cancelRunningAnimation();
            this.ngZone.runOutsideAngular(() => this.clearActive());
          } else if (s.startsWith('verse_changed') || s === 'chorus_start') {
            this.ngZone.runOutsideAngular(() => this.clearActive());
          }
        }
      })
    );

    this.subscriptions.push(
      this.settingService.readModeFontSizeSubject.subscribe(() => {
        this.ngZone.run(() => {
          this.fontSizeBase = this.settingService.getReadModeFontSize();
          this.fontSize = this.settingService.getReadModeFontSize();
          this.fontSizeStyle = this.calculateFontSizeStyle();
          this.lineHeightStyle = this.calculateLineHeightStyle();
        });
      })
    );

    this.subscriptions.push(
      this.settingService.fontFamilySubject.subscribe((f) => {
        this.ngZone.run(() => {
          this.readFontFamily = f;
        });
      })
    );

    this.subscriptions.push(
      this.settingService.fontWeightSubject.subscribe((w) => {
        this.ngZone.run(() => {
          this.readFontWeight = (w ?? 400);
        });
      })
    );

    this.subscriptions.push(
      this.router.events.subscribe(ev => {
        if (ev instanceof NavigationStart) {
          this.bottomSheet.dismiss();
        }
      })
    );

    this.subscriptions.push(
      this.route.paramMap.subscribe(params => {
        // 1. CLEAR PENDING ACTIONS
        clearTimeout(this.autoJumpTimeout);
        this.karaokeIndex.clear();
        this.orderedKaraokeSpans = [];
        this.sectionDataMap.clear();

        // 2. FORCE IMMEDIATE SCROLL TO TOP (Using the new instant flag)
        this.autoScrollService.scrollToTop(true);
        const actualScrollContainer = document.getElementById('hymn-scroll-container');
        if (actualScrollContainer) {
          actualScrollContainer.scrollTop = 0;
        }

        if (params.get('reference')) {
          setTimeout(() => {
            if(this.layoutService.singleView) {
              this.sidenavService.closeList();
            }
          }, 10);
          this.getJSON(params.get('reference')!);
          this.hymnSelectionService.selectedHymn = this.hymn;
          this.settingService.addToHistory(this.hymn.hymnNumber);
        } else {
          this.hymn = this.settingService.getLastHymn();
          this.hymnSelectionService.selectedHymn = this.hymn;
        }
        this.titleService.setTitle(`${translation.hymn} ${this.hymn.hymnNumber} - ${this.hymn.title} - ${translation.title_full}`);

        if(!this.continuousPlaybackService.active) {
          this.midiService.stop();
          this.autoScrollService.stopAutoScroll();
        }

        // 3. WAIT FOR DOM UPDATE, DOUBLE-FORCE SCROLL, THEN BUILD INDEX
        setTimeout(() => {
          this.autoScrollService.scrollToTop(true);
          if (actualScrollContainer) {
            actualScrollContainer.scrollTop = 0;
          }
          window.scrollTo(0, 0); // Failsafe for mobile browsers

          this.rebuildKaraokeIndex();
        }, 50);
      })
    );

    this.settingService.setHymnViewMode();
    this.elem = document.documentElement;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => { try { s.unsubscribe?.(); } catch {} });
    this.clearActive();
    clearTimeout(this.autoJumpTimeout);

    if (!this.continuousPlaybackService.active) {
      this.midiService.stop();
      this.autoScrollService.stopAutoScroll();
    }
  }

  public getJSON(reference: string) {
    this.hymn = hymnsDetailed[+reference - 1];
  }

  onSwipePrevious() {
    if (this.continuousPlaybackService.active || this.hymn.hymnNumber > 1) {
      if (!this.continuousPlaybackService.active) {
        this.ngZone.run(() => this.router.navigate([`${translation.path_hymn}/` + (this.hymn.hymnNumber - 1)])).then();
      } else {
        this.continuousPlaybackService.previous();
      }
    }
  }

  onSwipeNext() {
    if (this.continuousPlaybackService.active || this.hymn.hymnNumber < this.lastHymnNumber) {
      if (!this.continuousPlaybackService.active) {
        this.ngZone.run(() => this.router.navigate([translation.path_hymn + '/' + (this.hymn.hymnNumber + 1)])).then();
      } else {
        this.continuousPlaybackService.next();
      }
    }
  }

  changeFontSize() {
    this.layoutService.fontSizeSheetActive = true;

    const bottomSheetRef = this.bottomSheet.open(FontSizeComponent, {
      data: {
        fontSizeEnabled: true,
        linesPerVerseEnabled: false,
        fontSizeMin: this.fontSizeMin,
        fontSizeMax: this.fontSizeMax,
        currentFontSize: this.fontSize,
        fontSizeChange: new EventEmitter<number>(),
        fonts: this.settingService.getAvailableFonts(),
        currentFontFamily: this.settingService.getFontFamily(),
        fontFamilyChange: new EventEmitter<string | undefined>(),
        fontWeightEnabled: true,
        currentFontWeight: this.settingService.getFontWeight(),
        fontWeightChange: new EventEmitter<number | undefined>(),
        textOutlineToggleEnabled: true,
        textOutlineEnabled: this.settingService.getTextOutlineEnabled(),
        textOutlineChange: new EventEmitter<boolean>(),
        textAlignmentEnabled: true,
        currentTextAlignment: this.settingService.getTextAlignmentReadMode(),
        textAlignmentChange: new EventEmitter<string>()
      },
    });

    bottomSheetRef.instance.data.fontSizeChange?.subscribe((newFontSize: number) => {
      this.updateFontSize(newFontSize);
    });

    bottomSheetRef.instance.data.fontFamilyChange?.subscribe((fam: string | undefined) => {
      this.settingService.setFontFamily(fam);
    });

    bottomSheetRef.instance.data.fontWeightChange?.subscribe((w: number | undefined) => {
      this.settingService.setFontWeight(w);
    });

    bottomSheetRef.instance.data.textOutlineChange?.subscribe((enabled: boolean) => {
      this.settingService.setTextOutlineEnabled(enabled);
    });

    bottomSheetRef.instance.data.textAlignmentChange?.subscribe((al: string) => {
      this.settingService.setTextAlignmentReadMode(al);
    });

    bottomSheetRef.afterDismissed().subscribe(() => {
      this.layoutService.fontSizeSheetActive = false;
    });
  }

  previewFontSize(fontSize: number) {
    this.fontSize = fontSize;
    this.fontSizeStyle = this.calculateFontSizeStyle();
    this.lineHeightStyle = this.calculateLineHeightStyle();
  }

  updateFontSize(fontSize: number) {
    this.fontSizeBase = fontSize;
    this.settingService.setFontSize(fontSize);
    this.previewFontSize(fontSize);
  }

  protected readonly Hymn = Hymn;
  protected readonly translation = translation;
  protected readonly hymnsDetailed = hymnsDetailed;

  async onDoubleTap() {
    if(this.layoutService.fullscreenSupported()) {
      await this.layoutService.toggleFullScreen();
    }
  }
}
